import React from 'react';
import { getClothIcon } from '../clothIcons';
import { GenderSetting, getClothType } from '../config/clothTypes';

interface AttributesStepProps {
  gender: GenderSetting;
  clothType: string;
  attributes: Record<string, string[]>;
  onAttributesChange: (attributes: Record<string, string[]>) => void;
  onClear: () => void;
}

const AttributesStep: React.FC<AttributesStepProps> = ({
  gender,
  clothType,
  attributes,
  onAttributesChange,
  onClear,
}) => {
  const selectedClothType = clothType && clothType !== 'auto' ? getClothType(clothType, gender) : undefined;
  const attributeGroups = selectedClothType?.attributes || [];
  const hasSelection = Object.values(attributes).some((values) => values.length > 0);

  const toggleAttribute = (label: string, option: string) => {
    const current = attributes[label] || [];
    const next = current.includes(option)
      ? current.filter((value) => value !== option)
      : [...current, option];
    if (next.length === 0) {
      const { [label]: _removed, ...rest } = attributes;
      onAttributesChange(rest);
    } else {
      onAttributesChange({ ...attributes, [label]: next });
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl py-2">
      <div className="rounded-2xl border border-gray-200 bg-gray-50/50 p-6 shadow-sm">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-2xl shadow-sm">
              {selectedClothType ? getClothIcon(selectedClothType.id) : '✨'}
            </span>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-gray-700">Attributes</h4>
              <p className="mt-0.5 text-xs text-gray-500">
                {selectedClothType
                  ? `${selectedClothType.name} · select across categories`
                  : 'No garment selected'}
              </p>
            </div>
          </div>
          {hasSelection && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs font-semibold text-gray-400 transition-colors hover:text-red-500"
            >
              Clear attributes
            </button>
          )}
        </div>

        {selectedClothType ? (
          <div className="space-y-6">
            {attributeGroups.map((group) => {
              const selected = attributes[group.label] || [];
              return (
                <div key={group.key}>
                  <div className="mb-2.5 flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{group.label}</p>
                    {selected.length > 0 && (
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold text-secondary-700">
                        {selected.length} selected
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.options.map((option) => {
                      const isSelected = selected.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => toggleAttribute(group.label, option)}
                          className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${
                            isSelected
                              ? 'border-secondary-500 bg-secondary-500 text-white shadow-sm'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-secondary-400 hover:bg-secondary-50'
                          }`}
                        >
                          {isSelected && <i className="fas fa-check mr-1.5 text-[10px]" />}
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-gray-400">
              Tip: pick multiple attributes per category to generate design variations.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Go back and select a garment type to configure its attributes.</p>
        )}
      </div>
    </div>
  );
};

export default AttributesStep;
